import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gem, Hammer, Sparkles, Inbox, CheckSquare, Users, Handshake, Truck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { cardCls, Select, EmptyState } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";

function Stat({ icon: Icon, label, value, sub, to }) {
  const inner = (
    <div className={`${cardCls} p-6 h-full hover:border-bd-gold/40 transition-colors duration-300`}>
      <div className="flex items-start justify-between">
        <Icon size={22} strokeWidth={1.3} className="text-bd-gold" />
        <span className="font-serif text-4xl text-bd-ivory leading-none">{value}</span>
      </div>
      <p className="mt-5 text-sm text-bd-ivory/70">{label}</p>
      {sub && <p className="text-xs text-bd-ivory/35 mt-1">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [ventures, setVentures] = useState([]);
  const [scope, setScope] = useState("");

  useEffect(() => { api.get("/admin/ventures").then(({ data }) => setVentures(data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/admin/overview", { params: scope ? { venture_id: scope } : {} })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [scope]);

  if (!data) return <EmptyState>Loading overview…</EmptyState>;

  const fmt = (n) => "£" + Number(n || 0).toLocaleString("en-GB");

  return (
    <div data-testid="admin-overview">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="overline text-bd-gold mb-2">CEO Overview</p>
          <h1 className="font-serif text-3xl md:text-4xl text-bd-ivory">Good day, {user?.name?.split(" ")[0] || "Founder"}</h1>
          <p className="text-sm text-bd-ivory/45 mt-2">Group-level control centre.</p>
        </div>
        <div className="w-full sm:w-64">
          <Select data-testid="overview-scope" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="">Entire Group</option>
            {ventures.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Gem} label="Active ventures" value={data.active_ventures} to="/admin/ventures" />
        <Stat icon={Hammer} label="In development" value={data.ventures_in_development} to="/admin/ventures" />
        <Stat icon={Sparkles} label="Future ventures" value={data.future_ventures} to="/admin/ventures" />
        <Stat icon={Inbox} label="New enquiries" value={data.new_enquiries} sub={`${data.total_enquiries} total`} to="/admin/enquiries" />
        <Stat icon={CheckSquare} label="Open tasks" value={data.open_tasks} to="/admin/tasks" />
        <Stat icon={Users} label="Contacts" value={data.contacts_total} to="/admin/crm" />
        <Stat icon={Handshake} label="Partners" value={data.partners} to="/admin/crm" />
        <Stat icon={Truck} label="Suppliers" value={data.suppliers} to="/admin/crm" />
      </div>

      {/* Financials (demo) */}
      <div className={`${cardCls} p-6 mt-6`}>
        <div className="flex items-center gap-3 mb-6">
          <Wallet />
          <h2 className="font-serif text-2xl text-bd-ivory">Group financials</h2>
          <span className="px-2 py-0.5 text-[0.6rem] tracking-wider uppercase border border-bd-gold/40 text-bd-gold">Demo / placeholder</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div><p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Revenue</p><p className="font-serif text-3xl text-bd-ivory">{fmt(data.revenue_demo)}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Costs</p><p className="font-serif text-3xl text-bd-ivory">{fmt(data.costs_demo)}</p></div>
          <div><p className="text-xs uppercase tracking-wider text-bd-ivory/40 mb-1">Profit</p><p className="font-serif text-3xl text-bd-gold">{fmt(data.profit_demo)}</p></div>
        </div>
        <p className="text-xs text-bd-ivory/30 mt-5 flex items-center gap-2">
          <AlertTriangle size={13} /> Figures are placeholder demo data until accounting/payment systems are connected. No real financial data is shown.
        </p>
      </div>

      {/* Upcoming deadlines */}
      <div className={`${cardCls} p-6 mt-6`}>
        <h2 className="font-serif text-2xl text-bd-ivory mb-5">Upcoming deadlines</h2>
        {data.upcoming_deadlines?.length ? (
          <ul className="divide-y divide-white/5">
            {data.upcoming_deadlines.map((t) => (
              <li key={t.id} className="py-3 flex items-center justify-between">
                <span className="text-sm text-bd-ivory/80">{t.title}</span>
                <span className="text-xs text-bd-gold">{t.deadline}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-bd-ivory/35">No upcoming deadlines with dates set.</p>
        )}
      </div>
    </div>
  );
}

function Wallet() {
  return <span className="text-bd-gold"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M16 12h.01M2 10h20"/></svg></span>;
}
